// useCanisterInspector.ts — Fetches wasm hash + controllers + status per canister
// from the IC Dashboard public REST API.
//
// Mainnet-only: when VITE_DFX_NETWORK !== 'ic', the hook returns empty state
// and the UI shows a "Inspector available on mainnet only" placeholder.
//
// Endpoint used:
//   GET https://ic-api.internetcomputer.org/api/v3/canisters/<principal-text>
// Response fields used: status, module_hash (= wasm_module_hash), controllers, subnet_id
//
// ic-dashboard skill citation (ic-dashboard.md:77):
//   "GET /api/v3/canisters/<canister-id> returns wasm metadata"
//
// canhelp skill citation (canhelp.md:1-55):
//   Pattern: fetch IC dashboard API for a canister principal to inspect live state.
//   Anti-drift: canhelp skill uses icp-cli shell scripts. Dominus replaces those
//   with direct browser fetch() calls — same REST API, no CLI dependency.
//
// Anti-drift: no @icp-sdk/* imports. Uses plain fetch(). Dfx 0.25.x pinned.

import { useCallback, useEffect, useRef, useState } from 'react';
import type { CanisterRollup } from '../lib/adminAgent';

const IC_API_BASE = 'https://ic-api.internetcomputer.org';
const IS_MAINNET = import.meta.env.VITE_DFX_NETWORK === 'ic';

export interface CanisterInspection {
  // Text representation of the canister principal
  principalText: string;
  // "running" | "stopping" | "stopped" | "unknown"
  status: string;
  // Full hex wasm module hash (64 chars SHA-256). Empty string if not available.
  moduleHash: string;
  // Last 12 chars of moduleHash for compact display
  moduleHashShort: string;
  // List of controller principal text strings
  controllers: string[];
  // Subnet ID the canister lives on
  subnetId: string;
}

export interface UseCanisterInspectorReturn {
  // Keyed by canister principal text
  inspections: Record<string, CanisterInspection>;
  isMainnet: boolean;
  isLoading: boolean;
  errors: Record<string, string>;
  refresh: () => void;
}

interface IcApiCanisterResponse {
  status?: string;
  module_hash?: string | null;
  controllers?: string[];
  subnet_id?: string;
}

async function fetchCanisterInfo(principalText: string): Promise<CanisterInspection> {
  const url = `${IC_API_BASE}/api/v3/canisters/${encodeURIComponent(principalText)}`;
  const res = await fetch(url, { signal: AbortSignal.timeout(12_000) });

  if (!res.ok) {
    // 404 means canister not found on mainnet (not yet deployed or wrong ID)
    throw new Error(res.status === 404 ? 'Canister not found on mainnet.' : `IC API ${res.status}`);
  }

  const body = await res.json() as IcApiCanisterResponse;
  const moduleHash = body.module_hash ?? '';

  return {
    principalText,
    status: body.status ?? 'unknown',
    moduleHash,
    moduleHashShort: moduleHash.length >= 12 ? moduleHash.slice(-12) : moduleHash,
    controllers: Array.isArray(body.controllers) ? body.controllers : [],
    subnetId: body.subnet_id ?? '',
  };
}

export function useCanisterInspector(
  rollups: CanisterRollup[],
): UseCanisterInspectorReturn {
  const [inspections, setInspections] = useState<Record<string, CanisterInspection>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const isMountedRef = useRef(true);

  const fetchAll = useCallback(async () => {
    if (!IS_MAINNET || rollups.length === 0) return;

    setIsLoading(true);

    // Skip the sentinel principal (aaaaa-aa = IC management canister) — the
    // dashboard API returns 422 on it because it's not a user-deployed canister.
    // Any rollup that falls through to this sentinel has no real principal yet.
    const fetchable = rollups.filter((r) => r.principal.toText() !== 'aaaaa-aa');
    const results = await Promise.allSettled(
      fetchable.map((r) => fetchCanisterInfo(r.principal.toText())),
    );

    if (!isMountedRef.current) return;

    const nextInspections: Record<string, CanisterInspection> = {};
    const nextErrors: Record<string, string> = {};

    results.forEach((result, idx) => {
      const label = fetchable[idx].label;
      if (result.status === 'fulfilled') {
        nextInspections[label] = result.value;
      } else {
        const msg = result.reason instanceof Error
          ? result.reason.message
          : 'Fetch failed.';
        nextErrors[label] = msg;
      }
    });

    setInspections(nextInspections);
    setErrors(nextErrors);
    setIsLoading(false);
  }, [rollups]);

  const refresh = useCallback(() => { fetchAll(); }, [fetchAll]);

  useEffect(() => {
    if (!IS_MAINNET) return;

    isMountedRef.current = true;
    fetchAll();

    return () => {
      isMountedRef.current = false;
    };
  // Re-fetch when rollups list changes (new canister IDs available after health poll)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rollups.map((r) => r.principal.toText()).join(',')]);

  return { inspections, isMainnet: IS_MAINNET, isLoading, errors, refresh };
}
